import { useMemo } from 'react'

// @ts-ignore
import { UAParser } from '@amplitude/ua-parser-js'
import { ApolloClient, NormalizedCacheObject } from '@apollo/client'

import {
  BATCHING_ENABLED_DEFAULTS,
  createClient
} from 'src/apollo/createClient'
import { AppStatus, useAppPromoDataQuery } from 'src/apollo/sites'
import { useRestaurant } from 'src/shared/components/common/restaurant_context/RestaurantContext'

import { resources } from 'config'

declare global {
  interface Window {
    __APOLLO_STATE__: NormalizedCacheObject | undefined
  }
}

type Props = {
  skip?: boolean
  userAgent?: string
}

export type AppPromoData = {
  link?: string | null
  appLogo?: string
  appName: string
}

let clientSideAppPromoClient: ApolloClient<NormalizedCacheObject> | null = null

const getAppPromoClient = () => {
  const isServer = typeof window === 'undefined'

  // if SSR, always create a new client per request to prevent
  if (isServer) {
    return createClient({
      uri: resources.apiEndpoint,
      batchingOptions: BATCHING_ENABLED_DEFAULTS,
      timeout: resources.clientQueryTimeoutMs
    })
  }

  // if client, use a singleton to enable caching across components
  if (!clientSideAppPromoClient) {
    clientSideAppPromoClient = createClient({
      uri: resources.apiEndpoint,
      state: window.__APOLLO_STATE__,
      batchingOptions: BATCHING_ENABLED_DEFAULTS,
      timeout: resources.clientQueryTimeoutMs
    })
  }
  return clientSideAppPromoClient
}

export type AppPromoStatus = {
  appPromoData?: AppPromoData
  loading: boolean
}

/**
 * The app promo data plus whether it is still being fetched, for callers that
 * render something different depending on the answer and so cannot treat "not
 * yet known" as "no branded app".
 */
export const useBrandedAppPromoStatus = (props?: Props): AppPromoStatus => {
  const client = useMemo(() => getAppPromoClient(), [])
  const { restaurant } = useRestaurant()
  const { data: appData, loading } = useAppPromoDataQuery({
    variables: { siteGuid: restaurant.id! },
    skip: props?.skip || !restaurant.id,
    client,
    ssr: !!props?.userAgent,
    // App promo data rarely changes - use cache-first policy to prevent constant refetching
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first'
  })

  if (
    !appData ||
    !appData.appConfig ||
    appData.appConfig?.appStatus === AppStatus.Churned ||
    (!props?.userAgent && typeof window === 'undefined')
  ) {
    return { appPromoData: undefined, loading }
  }

  const parserResults = new UAParser(
    props?.userAgent || window.navigator.userAgent
  ).getResult()

  return {
    appPromoData: {
      link:
        parserResults.device.vendor === 'Apple'
          ? appData.appUrls?.appleAppStore
          : appData.appUrls?.googlePlay,
      appLogo: appData.appDisplay?.appIcon?.displaySrc,
      appName: appData.appConfig.appName || restaurant.name
    },
    loading
  }
}

export const useBrandedAppPromo = (props?: Props): AppPromoData | undefined =>
  useBrandedAppPromoStatus(props).appPromoData
